import { Box, Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import type {
  OrganisationEditFormOrganisation,
  OrganisationEditFormValues
} from '@/features/users/components/OrganisationEditForm';
import {
  getGetOrganisationQueryKey,
  useGetOrganisation,
  useUpdateProfile,
  useUploadLogo
} from '@/api/generated/organisations/organisations';
import { useListCountries, useListCurrencies } from '@/api/generated/reference-data/reference-data';
import { PageHeading, Toast } from '@/components/common';
import { useSetBreadcrumbExtra } from '@/components/layout/BreadcrumbContext';
import { OrganisationEditForm } from '@/features/users/components/OrganisationEditForm';
import { buildCountryOptions } from '@/utils/format';
import { useAuthenticatedImage } from '@/utils/useAuthenticatedImage';

export const AdminOrganizationEditPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading } = useGetOrganisation(id ?? '', { query: { enabled: !!id } });
  const org = data as unknown as OrganisationEditFormOrganisation | undefined;

  const { data: countries } = useListCountries();
  const { data: currencies } = useListCurrencies();
  const countryOptions = buildCountryOptions(countries);
  const currencyOptions =
    currencies?.map((c) => ({ value: c.code ?? '', label: `${c.code ?? ''} — ${c.name ?? ''}` })) ??
    [];

  const [saved, setSaved] = useState(false);

  useSetBreadcrumbExtra([org?.name ?? '', t('adminOrgEdit.title')]);

  const updateMutation = useUpdateProfile({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganisationQueryKey(id ?? '') });
        setSaved(true);
      }
    }
  });

  const uploadLogoMutation = useUploadLogo({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getGetOrganisationQueryKey(id ?? '') });
      }
    }
  });

  const logoSrc = useAuthenticatedImage(org?.logoKey ? `/api/v1/organisations/${id}/logo` : null);

  const handleCancel = () => navigate(`/admin/organizations/${id}`);
  const handleSubmit = (values: OrganisationEditFormValues) => {
    setSaved(false);
    if (!org) return;
    updateMutation.mutate({
      id: id ?? '',
      data: {
        name: values.name,
        address: values.address || undefined,
        currency: values.currency,
        phone: values.phone || undefined,
        email: values.email || undefined,
        city: values.city || undefined,
        postalCode: values.postalCode || undefined
      }
    });
  };

  const handleLogoSelected = (file: File) => {
    if (!id) return;
    uploadLogoMutation.mutate({ id, data: { file } });
  };

  if (isLoading) {
    return (
      <Center py={80}>
        <Loader color="var(--color-accent-primary)" />
      </Center>
    );
  }

  if (!org) {
    return (
      <Container size="lg" px={0}>
        <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
          {t('adminOrgDetail.notFound')}
        </Text>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        {saved && (
          <Box visibleFrom="sm" style={{ maxWidth: 680, width: '100%' }}>
            <Toast tone="success">{t('adminOrgEdit.saveSuccessMessage')}</Toast>
          </Box>
        )}

        <PageHeading
          title={t('adminOrgEdit.title')}
          subtitle={t('adminOrgEdit.subtitle', { name: org.name })}
        />

        {saved && (
          <Box hiddenFrom="sm">
            <Toast tone="success">{t('adminOrgEdit.saveSuccessMessage')}</Toast>
          </Box>
        )}

        <div style={{ maxWidth: 680, width: '100%' }}>
          <OrganisationEditForm
            organisation={org}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={updateMutation.isPending}
            isPaused={updateMutation.isPaused}
            countryOptions={countryOptions}
            currencyOptions={currencyOptions}
            logoSrc={logoSrc}
            onLogoSelected={handleLogoSelected}
            logoUploading={uploadLogoMutation.isPending}
          />
        </div>
      </Stack>
    </Container>
  );
};
